import React, { useMemo, useEffect } from 'react';
import { Button } from './ui/Button';

interface Detection {
  question: string;
  sessionId: string;
}

interface CommonFAQData {
  groupId: string;
  sourceId: string | null;
  parentId: string | null;
  originalQuestion: string | null;
  originalAnswer: string | null;
  detections: Detection[];
  totalCount: number;
  category: string;
  storedAnswer: string | null;
}

interface CommonFAQsModalProps {
  isOpen: boolean;
  onClose: () => void;
  faqs: CommonFAQData[];
  loading: boolean;
}

const CommonFAQsModal: React.FC<CommonFAQsModalProps> = ({
  isOpen,
  onClose,
  faqs,
  loading
}) => {
  // Calculate total detections
  const totalDetections = useMemo(() => {
    return faqs.reduce((sum, faq) => sum + faq.totalCount, 0);
  }, [faqs]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="relative max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col modal-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="heading-3">Common FAQs</h3>
            <p className="text-sm text-ink-secondary mt-1">
              {faqs.length} unique FAQs | {totalDetections} total detections
            </p>
          </div>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClose}
            className="!p-1"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-signature"></div>
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12 text-ink-muted">
              No FAQs detected for the selected period
            </div>
          ) : (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={faq.groupId || faq.sourceId || index}
                  className="p-4 rounded-lg border border-hairline bg-surface-subtle"
                >
                  {/* Header with count badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 text-sm font-bold text-ink bg-surface-sunken rounded-full">
                        {faq.totalCount}x
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-surface-sunken text-ink-secondary rounded-full">
                        {faq.category || 'Other'}
                      </span>
                    </div>
                    <span className="text-xs text-ink-muted">
                      #{index + 1}
                    </span>
                  </div>

                  {/* Original Question from Knowledge Base */}
                  {faq.originalQuestion && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-ink-muted mb-1 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 bg-ink-muted rounded-full"></span>
                        Original Question (Knowledge Base):
                      </div>
                      <div className="text-sm font-semibold text-ink pl-3 border-l-2 border-ink-border py-1">
                        {faq.originalQuestion}
                      </div>
                    </div>
                  )}

                  {/* Detected in Transcripts */}
                  <div>
                    <div className="text-xs font-medium text-ink-muted mb-2 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-ink-muted rounded-full"></span>
                      Detected in Transcripts ({faq.detections?.length || 0} detection{(faq.detections?.length || 0) !== 1 ? 's' : ''}):
                    </div>
                    <div className="pl-3 space-y-1 max-h-48 overflow-y-auto">
                      {faq.detections?.map((detection, dIndex) => (
                        <div
                          key={dIndex}
                          className="text-sm text-ink-secondary py-1 px-2 bg-surface rounded border-l-2 border-hairline flex justify-between items-start gap-2"
                        >
                          <span>"{detection.question}"</span>
                          <span className="text-xs text-ink-muted whitespace-nowrap">
                            [{detection.sessionId || 'N/A'}]
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* No original question - show note */}
                  {!faq.originalQuestion && (faq.detections?.length || 0) > 0 && (
                    <div className="mt-2 text-xs text-ink-muted italic">
                      (Original question not found in knowledge base)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommonFAQsModal;
